import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, MapPin, Phone, Users, Calendar, Zap, Upload, Check, ArrowRight, ArrowLeft } from 'lucide-react';

const STEPS = [
  { icon: Building2, title: 'Clinic Details', desc: 'Tell us about your practice' },
  { icon: MapPin, title: 'Location Setup', desc: 'Add your first location' },
  { icon: Phone, title: 'Twilio Connect', desc: 'Set up call tracking' },
  { icon: Users, title: 'Staff Invites', desc: 'Invite your team' },
  { icon: Calendar, title: 'Calendar', desc: 'Connect scheduling' },
  { icon: Zap, title: 'Automations', desc: 'Review workflows' },
  { icon: Upload, title: 'Import Data', desc: 'Upload patient records' },
];

export default function Onboarding() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    clinic_name: '',
    owner_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    timezone: 'America/New_York',
    location_name: 'Main Office',
    location_address: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleComplete = async () => {
    setSaving(true);
    try {
      await api().post('/api/onboarding/complete', form);
      await refreshUser();
      navigate('/dashboard');
    } catch {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await api().post('/api/onboarding/complete', {
        clinic_name: form.clinic_name || `${user?.name || 'My'}'s Clinic`,
        owner_name: form.owner_name || user?.name || 'Owner',
      });
      await refreshUser();
      navigate('/dashboard');
    } catch {
      setSaving(false);
    }
  };

  const goToImport = async () => {
    setSaving(true);
    try {
      await api().post('/api/onboarding/complete', {
        clinic_name: form.clinic_name || `${user?.name || 'My'}'s Clinic`,
        owner_name: form.owner_name || user?.name || 'Owner',
        location_name: form.location_name,
        location_address: form.location_address,
        timezone: form.timezone,
      });
      await refreshUser();
      navigate('/import-data');
    } catch {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Clinic Name</label>
              <input data-testid="onb-clinic-name" value={form.clinic_name} onChange={e => set('clinic_name', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="Bright Smiles Dental" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Owner Name</label>
              <input data-testid="onb-owner-name" value={form.owner_name} onChange={e => set('owner_name', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="Dr. Jane Smith" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <input data-testid="onb-email" value={form.email} onChange={e => set('email', e.target.value)} type="email"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="you@clinic.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                <input data-testid="onb-phone" value={form.phone} onChange={e => set('phone', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="+1 555 123 4567" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Timezone</label>
              <select data-testid="onb-timezone" value={form.timezone} onChange={e => set('timezone', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
              </select>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Location Name</label>
              <input data-testid="onb-loc-name" value={form.location_name} onChange={e => set('location_name', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="Main Office" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Address</label>
              <input data-testid="onb-loc-address" value={form.location_address} onChange={e => set('location_address', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="123 Main St, City, State" />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="text-center py-8">
            <Phone size={40} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Twilio Integration</h3>
            <p className="text-sm text-slate-500 mb-6">Connect your Twilio account for SMS and call tracking.<br/>You can configure this later in Settings.</p>
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-500 border border-slate-200">
              Twilio configuration will be available after setup. Skip for now and add your credentials in Settings.
            </div>
          </div>
        );
      case 3:
        return (
          <div className="text-center py-8">
            <Users size={40} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Invite Your Team</h3>
            <p className="text-sm text-slate-500 mb-6">Add front desk staff and treatment coordinators.<br/>You can invite team members anytime from Settings.</p>
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-500 border border-slate-200">
              Team invitations will be available in Settings after onboarding.
            </div>
          </div>
        );
      case 4:
        return (
          <div className="text-center py-8">
            <Calendar size={40} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Calendar Integration</h3>
            <p className="text-sm text-slate-500 mb-6">Connect Cal.com for appointment scheduling.<br/>You can set this up later in Settings.</p>
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-500 border border-slate-200">
              Cal.com integration available in Settings after onboarding.
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">These automations will be activated for your clinic:</p>
            {[
              { name: 'Missed Call Recovery', desc: 'Auto-text patients within 60s of missed call', on: true },
              { name: 'No-Show Follow-up', desc: 'Automatic outreach after no-shows', on: true },
              { name: 'Appointment Reminders', desc: '24h, 3h, and 30min reminders', on: true },
              { name: 'Unsold Treatment Reactivation', desc: 'Follow-up on quoted but unaccepted treatments', on: false },
            ].map((a) => (
              <div key={a.name} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900">{a.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{a.desc}</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${a.on ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {a.on ? 'Active' : 'Review needed'}
                </span>
              </div>
            ))}
          </div>
        );
      case 6:
        return (
          <div className="text-center py-8">
            <Upload size={40} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Import Patient Data</h3>
            <p className="text-sm text-slate-500 mb-6">
              Upload your last 6 months of patient data.<br/>
              Our AI will scan for revenue leaks and auto-populate your pipeline.
            </p>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <button
                data-testid="onb-import-data"
                onClick={goToImport}
                disabled={saving}
                className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all disabled:opacity-50"
              >
                Import CSV Now
              </button>
              <button
                data-testid="onb-skip-import"
                onClick={handleComplete}
                disabled={saving}
                className="w-full text-slate-500 text-sm hover:text-slate-700 py-2"
              >
                Skip — I'll import later
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar stepper */}
      <div className="hidden lg:flex w-72 bg-white border-r border-slate-200 flex-col p-6">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">DR</span>
          </div>
          <span className="text-lg font-bold text-slate-900">Setup</span>
        </div>
        <div className="space-y-1">
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-all ${
                i === step
                  ? 'bg-slate-900 text-white'
                  : i < step
                  ? 'text-emerald-600 bg-emerald-50'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                i === step ? 'bg-white/20 text-white' : i < step ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
              }`}>
                {i < step ? <Check size={12} /> : i + 1}
              </div>
              <span className="truncate">{s.title}</span>
            </button>
          ))}
        </div>
        <div className="mt-auto pt-6">
          <button onClick={handleSkip} disabled={saving}
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors">
            Skip setup entirely
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {/* Mobile step indicator */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
              <span>Step {step + 1} of {STEPS.length}</span>
              <div className="flex-1 bg-slate-200 rounded-full h-1">
                <div className="bg-slate-900 h-1 rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">{STEPS[step].title}</h2>
              <p className="text-sm text-slate-500 mt-1">{STEPS[step].desc}</p>
            </div>

            {renderStep()}

            {step < 6 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                <button
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-colors"
                >
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  data-testid="onb-next"
                  onClick={() => setStep(Math.min(6, step + 1))}
                  className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all"
                >
                  Next <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
